import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Box,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  useTheme,
  makeStyles,
  List,
} from '@material-ui/core';
import {
  SearchBar,
  SearchResult,
  SearchResultPager,
} from '@backstage/plugin-search-react';
import { DocsIcon, CatalogIcon } from '@backstage/core-components';
import { TaskSearchResultListItem } from '@internal/backstage-plugin-sev2';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { useContent } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, ClipboardList } from 'lucide-react';
import {
  AdrSearchResultListItem,
  AdrSearchResultListItemProps,
} from '@backstage-community/plugin-adr';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityReadPermission } from '@backstage/plugin-catalog-common/alpha';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    gap: theme.spacing(1),
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto',
    '&> button': {
      marginTop: theme.spacing(1),
    },
  },
  input: {
    flex: 1,
  },
  button: {
    '&:hover': {
      background: 'none',
    },
  },
  // Reduces default height of the modal, keeping a gap of 128px between the top and bottom of the page.
  paperFullWidth: { height: 'calc(100% - 128px)' },
  dialogActionsContainer: { padding: theme.spacing(1, 3) },
  viewResultsLink: { verticalAlign: '0.5em' },
}));

export const SearchModal = ({ toggleModal }: { toggleModal: () => void }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { transitions } = useTheme();
  const { focusContent } = useContent();

  const searchBarRef = useRef<HTMLInputElement | null>(null);

  // Only focus search bar on initial mount, not on every render
  useEffect(() => {
    searchBarRef?.current?.focus();
  }, []); // Empty dependency array - run only once on mount

  const handleCloseModal = useCallback(() => {
    toggleModal();
  }, [toggleModal]);

  const handleSearchResultClick = useCallback(() => {
    toggleModal();
    setTimeout(focusContent, transitions.duration.leavingScreen);
  }, [focusContent, transitions, toggleModal]);

  // This handler is called when "enter" is pressed or "View Full Results" is clicked
  const handleSearchBarSubmit = useCallback(() => {
    // Using ref to get the current field value without waiting for a query debounce
    const query = searchBarRef.current?.value ?? '';
    toggleModal();
    navigate(`/search?query=${query}`);
    setTimeout(focusContent, transitions.duration.leavingScreen);
  }, [navigate, toggleModal, focusContent, transitions]);

  return (
    <>
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <SearchBar
            className={classes.input}
            inputProps={{ ref: searchBarRef }}
            onSubmit={handleSearchBarSubmit}
            placeholder='Search by component name or task (e.g. "T123" or "T title")'
          />

          <IconButton onClick={handleCloseModal}>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          direction="row-reverse"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <Button
              className={classes.button}
              color="primary"
              endIcon={<ArrowRight size={18} />}
              onClick={handleSearchBarSubmit}
              disableRipple
            >
              View Full Results
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <SearchResult
          onClick={handleSearchResultClick}
          onKeyDown={handleSearchResultClick}
        >
          {({ results }) => (
            <List>
              {results.map(result => {
                // result.type is the index type defined by the collator.
                switch (result.type) {
                  case 'software-catalog':
                    return (
                      <CatalogSearchResultListItem
                        key={result.document.location}
                        result={result.document}
                        highlight={result.highlight}
                        icon={<CatalogIcon />}
                      />
                    );
                  case 'techdocs':
                    return (
                      <TechDocsSearchResultListItem
                        key={result.document.location}
                        result={result.document}
                        highlight={result.highlight}
                        icon={<DocsIcon />}
                      />
                    );
                  case 'task':
                    return (
                      <TaskSearchResultListItem
                        key={result.document.location}
                        result={result.document}
                        highlight={result.highlight}
                        icon={<ClipboardList size={20} />}
                      />
                    );
                  case 'adr':
                    const resultDocument =
                      result.document as AdrSearchResultListItemProps['result'];
                    return (
                      <RequirePermission
                        key={resultDocument?.location}
                        resourceRef={resultDocument?.entityRef}
                        permission={catalogEntityReadPermission}
                        errorPage={<Fragment />}
                      >
                        <AdrSearchResultListItem
                          key={resultDocument?.location}
                          result={resultDocument}
                          highlight={result.highlight}
                          icon={<DocsIcon />}
                        />
                      </RequirePermission>
                    );
                  default:
                    return null;
                }
              })}
            </List>
          )}
        </SearchResult>
      </DialogContent>
      <DialogActions className={classes.dialogActionsContainer}>
        <Grid container direction="row">
          <Grid item xs={12}>
            <SearchResultPager />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};
